import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '../components/SuperAdminLayout';
import '../styles/superadmin.css';

const BASE = 'https://menu.searchisend.in';

const ScanMenu = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('saas_superadmin_token');
    
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [scannedText, setScannedText] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState(''); // 'success', 'error', 'info'

    useEffect(() => {
        if (!token) {
            navigate('/superadmin_v2_secure_login');
            return;
        }
        fetchCustomers();
        // eslint-disable-next-line
    }, []);

    const fetchCustomers = async () => {
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=fetch_customers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                // Sort customers alphabetically
                const sorted = data.customers.sort((a, b) => a.restaurant_name.localeCompare(b.restaurant_name));
                setCustomers(sorted);
            }
        } catch (err) {
            console.error('Failed to fetch customers', err);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
            setScannedText('');
            setStatusMessage('');
        }
    };

    const handleScanMenu = async () => {
        if (!imageFile || !selectedCustomer) {
            setStatusMessage('Please select a customer and an image file.');
            setStatusType('error');
            return;
        }
        
        setIsScanning(true);
        setStatusMessage('Scanning menu... This may take a moment.');
        setStatusType('info');
        setScannedText('');
        
        try {
            const base64ImageData = imagePreview.split(',')[1];
            const payload = {
                image: base64ImageData,
                mimeType: imageFile.type
            };
 
            const apiUrl = `${BASE}/apimenu/v2/v2_scan_menu.php`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json' 
                },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
            
            const result = await response.json();
            if (result.candidates && result.candidates.length > 0) {
                const jsonText = result.candidates[0].content.parts[0].text;
                const parsedJson = JSON.parse(jsonText);
                setScannedText(JSON.stringify(parsedJson, null, 2));
                setStatusMessage('Scan complete! Please review the JSON below.');
                setStatusType('success');
            } else {
                throw new Error('No content received from the API.');
            }
        } catch (error) {
            setStatusMessage(`An error occurred: ${error.message}`);
            setStatusType('error');
        } finally {
            setIsScanning(false);
        }
    };

    const handleSaveMenu = async () => {
        if (!scannedText || !selectedCustomer) {
            setStatusMessage('No scanned data or customer selected to save.');
            setStatusType('error');
            return;
        }
        
        try {
            JSON.parse(scannedText);
        } catch (e) {
            setStatusMessage('The text is not valid JSON. Please correct it before saving.');
            setStatusType('error');
            return;
        }
        
        setIsSaving(true);
        setStatusMessage('Saving menu items to the database...');
        setStatusType('info');

        try {
            const response = await fetch(`${BASE}/apimenu/v2/v2_super_dashboard.php?action=save_scanned_menu`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customer_id: selectedCustomer,
                    menu_json: scannedText
                })
            });

            const result = await response.json();

            if (result.success) {
                setStatusMessage(result.message);
                setStatusType('success');
                setScannedText('');
            } else {
                throw new Error(result.message || 'An unknown error occurred.');
            }
        } catch (error) {
            setStatusMessage(`Failed to save: ${error.message}`);
            setStatusType('error');
        } finally {
            setIsSaving(false);
        }
    };

    const getStatusStyle = () => {
        if (statusType === 'error') return { color: 'var(--danger)', background: 'var(--danger-bg)', border: '1px solid var(--danger)' };
        if (statusType === 'success') return { color: 'var(--success)', background: 'var(--success-bg)', border: '1px solid var(--success)' };
        if (statusType === 'info') return { color: 'var(--info)', background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)' };
        return {};
    };

    return (
        <SuperAdminLayout>
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <div style={{ marginBottom: '2rem' }}>
                            <h2 className="sa-page-title">Scan Menu with AI</h2>
                            <p className="sa-page-subtitle">Automated Menu Extraction powered by Gemini AI.</p>
                        </div>

                        <div className="sa-panel" style={{ padding: '2.5rem' }}>
                            <div style={{ background: 'var(--bg-dark)', padding: '15px', borderRadius: 'var(--radius-sm)', borderLeft: '4px solid var(--accent-primary)', marginBottom: '2rem', display: 'flex', gap: '15px', alignItems: 'center' }}>
                                <i className="fas fa-info-circle fa-2x" style={{ color: 'var(--accent-primary)' }}></i>
                                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>
                                    Upload an image of a physical menu. The AI will convert it into a structured digital format for you to review and instantly save into the selected restaurant's database.
                                </p>
                            </div>

                            <div className="sa-form-group">
                                <label className="sa-form-label" style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ background: 'var(--bg-dark)', color: 'var(--accent-primary)', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>1</span>
                                    Select Customer / Restaurant
                                </label>
                                <select className="sa-select" style={{ width: '100%', background: 'var(--bg-card)' }} value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}>
                                    <option value="">-- Choose a Restaurant --</option>
                                    {customers.map(c => (
                                        <option key={c.id} value={c.id}>{c.restaurant_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="sa-form-group" style={{ marginTop: '2rem' }}>
                                <label className="sa-form-label" style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ background: 'var(--bg-dark)', color: 'var(--accent-primary)', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>2</span>
                                    Upload Menu Image
                                </label>
                                <div style={{ border: '2px dashed var(--border-color)', padding: '20px', borderRadius: 'var(--radius-sm)', textAlign: 'center', cursor: 'pointer', background: 'var(--bg-dark)', transition: 'all 0.2s' }}>
                                    <input type="file" accept="image/*" onChange={handleFileChange} style={{ color: 'var(--text-secondary)', display: 'block', width: '100%', cursor: 'pointer' }} />
                                    {!imagePreview && <p style={{ color: 'var(--text-secondary)', margin: '10px 0 0 0', fontSize: '0.85rem' }}>Click or drag a file to upload</p>}
                                </div>
                            </div>

                            {imagePreview && (
                                <div style={{ marginTop: '1rem', textAlign: 'center', background: 'var(--bg-dark)', padding: '15px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                                    <img src={imagePreview} alt="Menu preview" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                                </div>
                            )}

                            <button 
                                onClick={handleScanMenu} 
                                className="sa-primary-btn" 
                                disabled={isScanning || !imageFile || !selectedCustomer} 
                                style={{ width: '100%', marginTop: '2.5rem', padding: '15px', fontSize: '1.1rem', opacity: (isScanning || !imageFile || !selectedCustomer) ? 0.6 : 1 }}
                            >
                                {isScanning ? <><i className="fas fa-spinner fa-spin"></i> Processing Image...</> : <><i className="fas fa-magic"></i> Step 3: Extract Menu with AI</>}
                            </button>

                            {statusMessage && (
                                <div style={{ padding: '15px', borderRadius: 'var(--radius-sm)', marginTop: '20px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '10px', ...getStatusStyle() }}>
                                    <i className={`fas ${statusType === 'error' ? 'fa-exclamation-triangle' : statusType === 'success' ? 'fa-check-circle' : 'fa-info-circle'}`}></i>
                                    {statusMessage}
                                </div>
                            )}

                            {scannedText && (
                                <div style={{ marginTop: '3rem', borderTop: '1px solid var(--border-color)', paddingTop: '2.5rem' }}>
                                    <label className="sa-form-label" style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ background: 'var(--bg-dark)', color: 'var(--accent-primary)', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>4</span>
                                        Review & Edit Extracted Data (JSON)
                                    </label>
                                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '15px' }}><i className="fas fa-pen"></i> You can safely edit the text below to fix any AI hallucination errors before saving.</p>
                                    <textarea
                                        value={scannedText}
                                        onChange={(e) => setScannedText(e.target.value)}
                                        style={{ 
                                            width: '100%', height: '350px', fontFamily: '"JetBrains Mono", monospace', 
                                            background: '#0d1117', color: '#c9d1d9', border: '1px solid var(--border-color)', 
                                            padding: '20px', borderRadius: 'var(--radius-sm)', boxSizing: 'border-box',
                                            lineHeight: '1.5', fontSize: '14px'
                                        }}
                                    ></textarea>
                                    
                                    <button 
                                        onClick={handleSaveMenu} 
                                        className="sa-primary-btn" 
                                        disabled={isSaving}
                                        style={{ width: '100%', marginTop: '1.5rem', padding: '15px', background: 'var(--success)', border: 'none', fontSize: '1.1rem' }}
                                    >
                                        {isSaving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Step 5: Save Menu Items to Database</>}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
        </SuperAdminLayout>
    );
};

export default ScanMenu;

